import Decimal, {DecimalSource} from './break_infinity';
import {state} from 'lit/decorators.js';
import {ri} from './runner-idle';
import {UnlockableLitElement} from './unlockable-lit-element';
import {
  BehaviorSubject,
  bufferCount,
  combineLatest,
  distinctUntilChanged,
  filter,
  interval,
  last,
  map,
  Observable,
  pairwise,
  scan,
  shareReplay,
  single,
  startWith,
  tap,
} from 'rxjs';
import {Modifier} from './modifier';
import {fancy, formatResourceCapacity, formatResourceValue} from './utils';
import {compose, equals, identity, isEmpty, of, complement, omit} from 'ramda';
import * as D from './decimal_utils';

export class ValuefulUnlockableLitElement extends UnlockableLitElement {
  // #region Properties (30)

  /** A stream of the real values of this element. Changes are marbles. */
  private _value$!: Observable<Decimal>;

  @state() public capacity: Decimal = new Decimal(10);
  @state() public level: Decimal = new Decimal(1);
  @state() public value: Decimal = new Decimal(0);

  public baseCapacity$: BehaviorSubject<Decimal> = new BehaviorSubject<Decimal>(
    Decimal.POSITIVE_INFINITY
  );
  public capacityReductions$: BehaviorSubject<Record<string, Decimal>> =
    new BehaviorSubject({});
  reduceCapacity(tag: string, amount: DecimalSource) {
    this.capacityReductions$.next({
      ...this.capacityReductions$.value,
      [tag]: (this.capacityReductions$.value[tag] ?? new Decimal(0)).plus(
        amount
      ),
    });
  }
  removeCapacityReduction(
    tag: string,
    amount: DecimalSource = Decimal.POSITIVE_INFINITY
  ) {
    if (!(amount instanceof Decimal)) {
      amount = new Decimal(amount);
    }
    if (amount.eq(Decimal.POSITIVE_INFINITY)) {
      this.capacityReductions$.next(
        omit([tag], this.capacityReductions$.value)
      );
      return;
    }
    this.reduceCapacity(tag, amount.negate());
  }
  public baseValue$: BehaviorSubject<Decimal> = new BehaviorSubject<Decimal>(
    new Decimal(0)
  );
  public beforeChange: (newValue: Decimal) => void = (_newValue: Decimal) => {};
  public onGain = (_newValue: Decimal) => {};
  public onLoss = (_newValue: Decimal) => {};
  /** A stream of the real capacity of this element. Changes are marbles. */
  public capacity$!: Observable<Decimal>;
  public capacityFormatter: (capacity: DecimalSource) => string =
    formatResourceCapacity;
  public capacityModifiers: Modifier[] = [];
  public capacityModifiers$: BehaviorSubject<Modifier[]> = new BehaviorSubject(
    [] as Modifier[]
  );
  /** Is this at capacity? */
  public full$!: Observable<boolean>;
  /** Has this ever not been full */
  public everNotFull$!: Observable<boolean>;
  public initialCapacity: Decimal = new Decimal(0);
  public initialValue: Decimal = new Decimal(0);
  public level$: BehaviorSubject<Decimal> = new BehaviorSubject(new Decimal(1));
  public maxValue: Decimal = new Decimal(0);
  /** A stream of the highest historical value of this element. */
  public maxValue$!: Observable<Decimal>;
  public minimum = new Decimal(0);
  /** Callback function triggered when value is set to capacity or higher.
   *  Note that this will run each time the value is set to the capacity or above,
   *  including when it's already at max capacity!
   */
  public onMaxCapacity: () => void = () => {};
  /** Callback function triggered when value is set to minimum or lower.
   *  Note that this will run each time the value is set to the minimum or below,
   *  including when it's already at minimum!
   */
  public onMinimum: () => void = () => {};
  /** How much room does this still have? */
  vacancy$!: Observable<Decimal>;
  vacancy!: Decimal;
  public valueFormatter: (value: DecimalSource) => string = formatResourceValue;

  public get baseCapacity(): Decimal {
    return this.baseCapacity$.value;
  }

  public set baseCapacity(value: Decimal) {
    this.baseCapacity$.next(value);
  }

  public get baseValue(): Decimal {
    return this.baseValue$.value;
  }

  public set baseValue(value: Decimal) {
    this.baseValue$.next(value);
  }

  public get value$(): Observable<Decimal> {
    return this._value$;
  }

  public set value$(value: Observable<Decimal>) {
    this._value$ = value.pipe(distinctUntilChanged(D.eq), shareReplay(1));
    this.setupPostValueStreams();
  }

  // #endregion Properties (30)

  // #region Constructors (1)

  constructor() {
    super();
    this.value$ = this.baseValue$.pipe(distinctUntilChanged());
  }

  // #endregion Constructors (1)

  // #region Public Methods (10)

  public capacityString() {
    return `Base capacity: ${this.capacityFormatter(this.baseCapacity)}
        ${this.capacityModifiers.map(
          (mod) => `<br>&nbsp;&nbsp;&nbsp;&nbsp;${mod.string()}`
        )}
        <br>Capacity: ${this.capacityFormatter(this.capacity)}
        ${
          Object.keys(this.capacityReductions$.value).length > 0
            ? `<br>Capacity changed by:${Object.entries(
                this.capacityReductions$.value
              ).reduce(
                (acc, [k, v]) =>
                  acc +
                  `<br>&nbsp;&nbsp;&nbsp;&nbsp;${k}: ${v.negate().toString()}`,
                ''
              )}`
            : ''
        }
        `;
  }

  public levelUp() {
    this.level$.next(this.level.plus(1));
    console.log(`Level up ${this.name} to ${this.level}`);
    ri.log(
      `<span>${fancy(
        this.name,
        this.displayName
      )} leveled up! ${this.level.minus(1)} -> ${this.level}</span>`,
      ['levelup']
    );
  }

  public override loadFromJSON(data) {
    super.loadFromJSON(data);
    this.level = new Decimal(data.level);
    this.baseCapacity = new Decimal(data.baseCapacity);
    this.baseValue$.next(new Decimal(data.maxValue as Decimal));
    this.baseValue = new Decimal(data.baseValue);
  }

  /**
   * For valueful unlockables (Resources, Stats right now),
   * sets the values back to initial values and does other work.
   * TODO Replace/subclass/clean this
   */
  public resetToInitials() {
    this.baseCapacity$.next(this.initialCapacity);
    this.baseValue$.next(this.initialValue);
  }

  public setCapacity(capacity: Decimal) {
    this.baseCapacity$.next(capacity);
  }

  /**
   * For valueful unlockables (Resources, Stats right now),
   * sets the initial values and does other work.
   * TODO Replace/subclass/clean this
   */
  public setInitials() {
    this.initialCapacity = this.baseCapacity;
    this.initialValue = this.baseValue;
    this.setupPostValueStreams();
  }

  public setValue(value: DecimalSource) {
    value = new Decimal(value);
    if (this.capacity.isFinite()) {
      this.baseValue$.next(value.clamp(this.minimum, this.capacity));
    } else {
      this.baseValue$.next(value.max(this.minimum));
    }
    return value;
  }
  public addValue(value: DecimalSource) {
    this.setValue(new Decimal(value).plus(this.baseValue$.value));
  }

  public override setup(
    overrides: Partial<ValuefulUnlockableLitElement>
  ): void {
    super.setup(overrides);
    this.subscribe('level', this.level$);
    this.subscribe('value', this.value$);
    this.subscribe('maxValue', this.maxValue$);
    this.subscribe('capacity', this.capacity$);
    this.subscribe('vacancy', this.vacancy$);
    // fix the capacity modifiers to be more efficient
    this.subscribe('capacityModifiers', this.capacityModifiers$);

    this.full$.pipe(filter(identity)).subscribe(this.onMaxCapacity.bind(this));
    this.value$
      .pipe(filter(D.eq(this.minimum)))
      .subscribe(this.onMinimum.bind(this));
    this.value$
      .pipe(pairwise())
      .pipe(map(([last, current]) => current.minus(last)))
      .subscribe(
        ((delta) => {
          switch (delta.sign()) {
            case -1:
              this.onLoss(delta);
              break;
            case 1:
              this.onGain(delta);
              break;
            case 0:
              break;
            default:
              break;
          }
        }).bind(this)
      );
    // TODO add onminimum, beforegain, etc.
  }

  public setupPostValueStreams() {
    // console.log(`Setup postvalue streams for ${this.name}`);
    // TODO streamify capacity mods
    // Little bit complicated. I'm going to have to make the capacity modifiers be streams?
    // and have them tick based on what they require? tricky.
    // e.g. Mods.plusLevel is depending on the resources level$,
    // plusPer cases about value$, etc.
    // For now, it updates on a timer and is just distincted out
    const thisName = this.name;
    this.capacity$ = combineLatest([
      this.baseCapacity$,
      this.capacityReductions$.pipe(
        filter(complement(isEmpty)),
        startWith([0]),
        map(compose(D.sum, Object.values))
      ),
      this.capacityModifiers$,
      interval(100),
    ]).pipe(
      map(([base, reduction, modifiers]) =>
        modifiers.reduce((acc, mod) => mod.value(acc), base).minus(reduction)
      ),
      distinctUntilChanged(D.eq),
      shareReplay(1)
    );
    this.vacancy$ = combineLatest([this.value$, this.capacity$]).pipe(
      // tap(([value, capacity]) =>
      // console.log(
      //   `Update to ${this.name} - value: ${value}, capacity: ${capacity}`
      // )
      // ),
      map(([value, capacity]) => capacity.minus(value)),
      distinctUntilChanged(),
      // tap((val) => console.log(`vacancy: ${val}`)),
      shareReplay(1)
    );
    this.full$ = this.vacancy$.pipe(
      map((vacancy) => vacancy.eq(0)),
      distinctUntilChanged(),
      shareReplay(1)
    );
    this.maxValue$ = this.value$.pipe(
      distinctUntilChanged((prev, curr) => {
        return prev.gte(curr);
      }),
      shareReplay(1)
    );
  }

  public override toJSON() {
    return {
      ...super.toJSON(),
      level: this.level,
      baseValue: this.baseValue,
      maxValue: this.maxValue,
      baseCapacity: this.baseCapacity,
    };
  }

  // #endregion Public Methods (10)
}
